import React, { useEffect, useMemo, useState } from 'react';
import { api, type Room, type BookingItem } from '../lib/api';
import { alarm, type AlarmState } from '../lib/alarm';
import { useBookings } from '../lib/bookingsContext';
import {
  formatDuration, groupByRoom, msRemaining, sortRoomsForTracker, stateOf,
  STALE_OVERTIME_MS, type BookingState,
} from '../lib/bookings';
import { StartBookingModal } from './StartBookingModal';
import { RoomOrderModal } from './RoomOrderModal';
import { useAuth } from '../lib/auth';

const EXTEND_PRESETS = [15, 30, 60];

interface Props {
  rooms: Room[];
  /** Called after the room order changes, so the caller can refetch rooms. */
  onRoomsChanged?: () => void;
}

/**
 * The board. Polling, the corrected clock and the alarm all live in
 * BookingsProvider above the tab switcher — this is a view onto that state,
 * so navigating away no longer stops the module doing its job.
 */
export function RoomTrackerScreen({ rooms, onRoomsChanged }: Props) {
  const { user } = useAuth();
  const { bookings, warningMinutes, loading, error: pollError, nowMs, refresh } = useBookings();

  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [startRoom, setStartRoom] = useState<Room | null>(null);
  const [alarmState, setAlarmState] = useState<AlarmState>(alarm.state);
  const [orderOpen, setOrderOpen] = useState(false);
  const canOrder = user?.role === 'MANAGER' || user?.role === 'ADMIN';

  useEffect(() => alarm.subscribe(() => setAlarmState(alarm.state)), []);

  const withState = useMemo(
    () => bookings.map(b => ({ booking: b, state: stateOf(b, nowMs, warningMinutes) })),
    [bookings, nowMs, warningMinutes],
  );

  const expired = withState.filter(x => x.state === 'expired');
  const warning = withState.filter(x => x.state === 'warning');
  const stale   = expired.filter(x => -msRemaining(x.booking, nowMs) > STALE_OVERTIME_MS);

  const busyProfileIds = useMemo(() => new Set(bookings.map(b => b.profileId)), [bookings]);
  // Room Tracker's own sequence. Rooms nobody has placed keep their Room Plan
  // position at the end, so a new room is never missing from the board.
  const orderedRooms = useMemo(() => sortRoomsForTracker(rooms), [rooms]);
  const groups = useMemo(() => groupByRoom(orderedRooms, bookings), [orderedRooms, bookings]);

  async function extend(id: string, addMinutes: number) {
    setBusyId(id);
    setActionError(null);
    try {
      await api.extendBooking(id, addMinutes);
      await refresh();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  async function end(booking: BookingItem) {
    const overdue = msRemaining(booking, nowMs) < 0;
    const label = `${booking.workingName} in room ${booking.roomNumber}`;
    if (!window.confirm(`End the booking for ${label}?${overdue ? '\n\nThis room is currently overdue.' : ''}`)) return;
    setBusyId(booking.id);
    setActionError(null);
    try {
      await api.endBooking(booking.id);
      await refresh();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  const audioUnavailable = alarmState !== 'ready';
  const error = actionError ?? pollError;

  return (
    <div className="rt-screen">
      {audioUnavailable && (
        <div className="rt-audio-banner">
          <span className="rt-audio-icon">🔇</span>
          <span>
            {alarmState === 'unsupported'
              ? 'This browser cannot play the alarm. Watch the board — overdue rooms turn red.'
              : alarmState === 'error'
                ? 'The alarm sound failed to load. Watch the board — overdue rooms turn red.'
                : 'Alarm sound is not enabled yet. Click to turn it on.'}
          </span>
          <button type="button" className="rt-audio-enable" onClick={() => { void alarm.unlock(); }}>
            Enable sound
          </button>
        </div>
      )}

      <div className="rt-summary">
        <span className="rt-summary-item">Rooms <strong>{rooms.length}</strong></span>
        <span className="rt-summary-item">
          In use <strong>{new Set(bookings.map(b => b.roomId)).size}</strong>
        </span>
        <span className="rt-summary-item">Bookings <strong>{bookings.length}</strong></span>
        <span className="rt-summary-sep">|</span>
        <span className="rt-summary-item warning">Due soon <strong>{warning.length}</strong></span>
        <span className="rt-summary-item expired">Overdue <strong>{expired.length}</strong></span>
        <span className="rt-summary-spacer" />
        {canOrder && (
          <button
            type="button"
            className="rt-test-alarm"
            onClick={() => setOrderOpen(true)}
            title="Set the order rooms appear in on this board (Room Plan is unaffected)"
          >
            ↕ Room order
          </button>
        )}
        <button
          type="button"
          className="rt-test-alarm"
          onClick={() => { void alarm.unlock().then(() => alarm.fire()); }}
          title="Play the alarm once, to check this terminal's sound before a shift"
        >
          🔔 Test alarm
        </button>
      </div>

      {stale.length > 0 && (
        <div className="rt-stale-banner">
          <strong>{stale.length} booking{stale.length === 1 ? '' : 's'} overdue by more than two hours.</strong>
          {' '}Likely forgotten rather than running late — check and end:
          <ul className="rt-stale-list">
            {stale.map(({ booking }) => (
              <li key={booking.id}>
                Room {booking.roomNumber} · {booking.workingName} ·{' '}
                {formatDuration(msRemaining(booking, nowMs))} over
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && <div className="rt-error">{error}</div>}
      {loading && bookings.length === 0 && <div className="loading">Loading board…</div>}

      <div className="rt-grid">
        {groups.map(group => {
          const worst = group.bookings.reduce<BookingState | null>((acc, s) => {
            const st = stateOf(s, nowMs, warningMinutes);
            if (st === 'expired' || acc === 'expired') return 'expired';
            if (st === 'warning' || acc === 'warning') return 'warning';
            return 'running';
          }, null);

          const room = orderedRooms.find(r => r.id === group.roomId);

          return (
            <div key={group.roomId} className={`rt-room ${worst ?? 'free'}`}>
              <div className="rt-room-header">
                <div>
                  <span className="rt-room-num">Room {group.roomNumber}</span>
                  {group.roomDisplayName && <span className="rt-room-name">{group.roomDisplayName}</span>}
                </div>
                <button
                  type="button"
                  className="rt-add"
                  onClick={() => room && setStartRoom(room)}
                  disabled={!room}
                  title="Start a booking in this room"
                >
                  + Start
                </button>
              </div>

              {group.bookings.length === 0 && <div className="rt-room-empty">Free</div>}

              {group.bookings.map(booking => {
                const state = stateOf(booking, nowMs, warningMinutes);
                const remaining = msRemaining(booking, nowMs);
                const over = remaining < 0;
                const isBusy = busyId === booking.id;
                return (
                  <div key={booking.id} className={`rt-booking ${state}`}>
                    <div className="rt-booking-top">
                      <span className="rt-provider-label">{booking.workingName}</span>
                      <span className={`rt-countdown ${state}`}>
                        {over ? '+' : ''}{formatDuration(remaining)}
                      </span>
                    </div>
                    <div className="rt-booking-meta">
                      {booking.durationMinutes} min · started{' '}
                      {new Date(booking.startedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      {over && <span className="rt-overdue-tag">OVERDUE</span>}
                    </div>
                    <div className="rt-booking-actions">
                      {EXTEND_PRESETS.map(m => (
                        <button
                          key={m}
                          type="button"
                          className="rt-btn"
                          disabled={isBusy}
                          onClick={() => extend(booking.id, m)}
                        >
                          +{m}m
                        </button>
                      ))}
                      <button
                        type="button"
                        className="rt-btn end"
                        disabled={isBusy}
                        onClick={() => end(booking)}
                      >
                        {isBusy ? '…' : 'End'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {rooms.length === 0 && <div className="loading">No rooms configured.</div>}

      {orderOpen && (
        <RoomOrderModal
          rooms={rooms}
          onClose={() => setOrderOpen(false)}
          onSaved={() => { setOrderOpen(false); onRoomsChanged?.(); }}
        />
      )}

      {startRoom && (
        <StartBookingModal
          roomId={startRoom.id}
          roomNumber={startRoom.number}
          roomDisplayName={startRoom.displayName}
          busyProfileIds={busyProfileIds}
          onClose={() => setStartRoom(null)}
          onStarted={() => { setStartRoom(null); void refresh(); }}
        />
      )}
    </div>
  );
}
